/**
 * CreateNotebookModal - Modal for creating new studios
 *
 * Features:
 * - Title input with auto-suggestion from files
 * - Drag and drop file upload
 * - Keyboard shortcuts (Cmd+Enter, Escape)
 *
 * @module components/modals/CreateNotebookModal
 */

import React, { useState, useRef, useEffect } from 'react';

import { BookOpen, FolderOpen, CheckCircle2, Upload, X, Loader2, Plus } from 'lucide-react';

import { getFileIcon, getFileTypeTooltip, formatFileSize } from '@/pages/intelligence-studio/utils/fileUtils';
import { generateSmartVaultTitle } from '@/pages/intelligence-studio/utils/vaultTitle';

/**
 * CreateNotebookModal Component
 *
 * @param {Object} props
 * @param {Function} props.onClose - Close modal
 * @param {Function} props.onCreate - Create studio (title, files)
 */
const CreateNotebookModal = ({ onClose, onCreate }) => {
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasUserEditedTitle, setHasUserEditedTitle] = useState(false);
  const fileInputRef = useRef(null);
  const titleInputRef = useRef(null);

  const hasFiles = files.length > 0;
  const canCreate = title.trim().length > 0 && hasFiles;
  const totalSize = files.reduce((acc, f) => acc + f.size, 0);

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') onClose();
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canCreate) handleCreate();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [title, files, canCreate]);

  // Focus title input on mount
  useEffect(() => {
    titleInputRef.current?.focus();
  }, []);

  const handleCreate = async () => {
    if (!canCreate) return;
    setIsCreating(true);
    try {
      await onCreate(title, files);
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileSelect = e => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = newFiles => {
    setFiles(prev => [...prev, ...newFiles]);
    // ✅ Enterprise: Smart multi-file naming (never "first file only")
    // Only apply if user hasn't typed a custom title yet.
    if (!hasUserEditedTitle) {
      const combined = [...files, ...newFiles];
      if (combined.length > 0) setTitle(generateSmartVaultTitle(combined));
    }
  };

  const removeFile = index => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-pink-500 opacity-90" />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }}
          />

          <div className="relative px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Create New Studio</h2>
                  <p className="text-sm text-white/80">Organize your documents intelligently</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Studio Name Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-neutral-300 mb-2">
              Studio Name
            </label>
            <div className="relative">
              <input
                ref={titleInputRef}
                type="text"
                value={title}
                onChange={e => {
                  setHasUserEditedTitle(true);
                  setTitle(e.target.value);
                }}
                placeholder="e.g., Q4 Contracts, Legal Documents..."
                className="w-full px-4 py-3 pl-11 border-2 border-slate-200 dark:border-neutral-700 rounded-xl bg-slate-50 dark:bg-neutral-800 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                <FolderOpen className="h-5 w-5 text-slate-400" />
              </div>
              {title && (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
              )}
            </div>
          </div>

          {/* File Drop Zone */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-neutral-300 mb-2">
              Documents <span className="text-rose-500 font-normal">(required)</span>
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 ${
                isDragging
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 scale-[1.02]'
                  : 'border-slate-300 dark:border-neutral-600 hover:border-amber-400 hover:bg-slate-50 dark:hover:bg-neutral-800/50'
              }`}
            >
              <div className="p-8 text-center">
                <div
                  className={`inline-flex p-4 rounded-2xl mb-4 transition-colors ${
                    isDragging
                      ? 'bg-amber-100 dark:bg-amber-900/40'
                      : 'bg-slate-100 dark:bg-neutral-800'
                  }`}
                >
                  <Upload
                    className={`h-8 w-8 ${isDragging ? 'text-amber-600' : 'text-slate-400'}`}
                  />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-neutral-300 mb-1">
                  {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                </p>
                <p className="text-xs text-slate-400 dark:text-neutral-500">
                  PDF, Word (.docx), Excel, CSV, and images
                </p>
            </div>
            {!hasFiles && (
              <p className="mt-2 text-xs text-rose-500">
                Add at least one source file to create a studio.
              </p>
            )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.tiff,.tif"
            />
          </div>

          {/* Selected Files List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700 dark:text-neutral-300">
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </span>
                <span className="text-slate-400">{formatFileSize(totalSize)} total</span>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-2 pr-1">
                {files.map((file, i) => {
                  const iconSrc = getFileIcon(file.name);
                  const isCDNIcon = typeof iconSrc === 'string' && iconSrc.startsWith('http');
                  const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
                  const fileTooltip = getFileTypeTooltip(fileExt);

                  return (
                    <div
                      key={i}
                      className="group flex items-center gap-3 p-3 bg-slate-50 dark:bg-neutral-800 rounded-xl hover:bg-slate-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      {isCDNIcon ? (
                        <img
                          src={iconSrc}
                          alt={file.name}
                          title={fileTooltip}
                          className="w-8 h-8 object-contain"
                          style={{ cursor: 'help' }}
                          onError={e => {
                            e.target.style.display = 'none';
                            const fallback = document.createElement('span');
                            fallback.className = 'text-2xl';
                            fallback.textContent = '📎';
                            fallback.title = fileTooltip;
                            fallback.style.cursor = 'help';
                            e.target.parentNode?.appendChild(fallback);
                          }}
                        />
                      ) : (
                        <span className="text-2xl" title={fileTooltip} style={{ cursor: 'help' }}>
                          {iconSrc}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-neutral-200 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          removeFile(i);
                        }}
                        className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-neutral-800/50 border-t border-slate-200 dark:border-neutral-700 flex items-center justify-between">
          <p className="text-xs text-slate-400">
            <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-neutral-700 rounded text-[10px]">
              ⌘
            </kbd>
            <span className="mx-1">+</span>
            <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-neutral-700 rounded text-[10px]">
              ↵
            </kbd>
            <span className="ml-1.5">to create</span>
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-slate-600 dark:text-neutral-300 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!canCreate || isCreating}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-600 to-orange-500 text-white rounded-xl font-medium hover:from-amber-700 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-amber-500/25 flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  <span>Create Studio</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNotebookModal;